import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs/operators';
import { Logger } from '../../_core/logger.service';
import { ConfigService } from '../../_core/service/config.service';
import {
  FormType,
  PIP_MASTER_STATES_LIST,
  RequestProgressStep,
  UserRole,
} from '../../config/constants';
import { PlaygroundService } from '../playground/playground.service';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  role;
  request;
  formTypeStep;
  requestKey;

  requestFormTypeList = [];

  currentStep: string = '';
  currentFormType: string = '';
  phoneNumber: string = '';
  um_uim_stepCompletionStatus: { [step: string]: boolean } = {
    [RequestProgressStep.SELECTION_OF_COVERAGES]: false,
    [RequestProgressStep.REVIEW_STATE_SELECTIONS]: false,
    [RequestProgressStep.OUTSTANDING_STATE_SELECTIONS]: false,
    [RequestProgressStep.ELECTRONIC_SIGNATURE]: false,
    [RequestProgressStep.NON_DIGITAL_ELECTION_DOWNLOAD]: false,
    [RequestProgressStep.NON_DIGITAL_ELECTION_SHIP_FORMS]: false,
  };
  pip_stepCompletionStatus: { [step: string]: boolean } = {
    [RequestProgressStep.SELECTION_OF_COVERAGES]: false,
    [RequestProgressStep.REVIEW_STATE_SELECTIONS]: false,
    [RequestProgressStep.OUTSTANDING_STATE_SELECTIONS]: false,
    [RequestProgressStep.ELECTRONIC_SIGNATURE]: false,
    [RequestProgressStep.NON_DIGITAL_ELECTION_DOWNLOAD]: false,
    [RequestProgressStep.NON_DIGITAL_ELECTION_SHIP_FORMS]: false,
  };

  updateStepCompletionStatus(
    formType: FormType,
    step: RequestProgressStep,
    completed: boolean,
  ) {
    if (formType === FormType.UM_UIM) {
      this.um_uim_stepCompletionStatus[step] = completed;
    } else {
      this.pip_stepCompletionStatus[step] = completed;
    }
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private playgroundService: PlaygroundService,
  ) {}

  getRequestKey(): string | null {
    if (this.requestKey) {
      return this.requestKey;
    }
    const params = new URLSearchParams(window.location.search);
    const requestKey = params.get('key');
    this.requestKey = requestKey;
    return requestKey;
  }
  getValidationKey(): boolean | null {
    const params = new URLSearchParams(window.location.search);
    const validationKey = params.get('validation');
    return validationKey === 'false' ? false : true;
  }

  getUserRole(): string | null {
    //Removed the previous Userrole Logic as it was redundant as always returns userrole to be insured.
    let requestKey = this.getRequestKey();
    if (!requestKey) {
      return null;
    }
    if (requestKey.charAt(0).toLowerCase() == 'i') {
      return 'insured';
    }
    if (requestKey.charAt(0).toLowerCase() == 'b') {
      return 'broker';
    }
    return null;
  }
  updateRequestDetails(updatedDetails: any): boolean {
    if (!this.request) {
      return false;
    }
    try {
      if (!this.request.requestDetails) {
        this.request.requestDetails = {};
      }
      if (updatedDetails.accountDetail) {
        if (!this.request.requestDetails.accountDetail) {
          this.request.requestDetails.accountDetail = {};
        }
        // Only update specific account detail properties
        this.request.requestDetails.accountDetail = {
          ...this.request.requestDetails.accountDetail,
          ...updatedDetails.accountDetail,
        };
        const { accountDetail, ...otherDetails } = updatedDetails;
        updatedDetails = otherDetails;
      }

      // Update other request details if any remain
      if (Object.keys(updatedDetails).length > 0) {
        this.request.requestDetails = {
          ...this.request.requestDetails,
          ...updatedDetails,
        };
      }
      return true;
    } catch (error) {
      console.error('Error updating request details:', error);
      return false;
    }
  }

  checkIfFormIsCompleted() {
    if (
      window.location.href.search('skipRequestStatusCheck=true') >= 0 &&
      (window.location.origin.toLowerCase().includes('dev.digisign') ||
        window.location.origin.toLowerCase().includes('localhost') ||
        window.location.origin.toLowerCase().includes('qa.digisign'))
    ) {
      Logger.log('Skipping request status check as per URL parameter');
      return;
    }
    let formTypeStepList = this.formTypeStep;
    if (formTypeStepList.length === 1) {
      let formType = formTypeStepList[0].formType.toLowerCase();
      let step = formTypeStepList[0].step;
      if (this.isFormTypeCompleted(step)) {
        this.router.navigate(['thank-you'], {
          queryParamsHandling: 'preserve',
        });
        // this.router.navigate(['404']);
        return;
      }
    }

    if (formTypeStepList.length === 2) {
      let formType1 = formTypeStepList[0].formType.toLowerCase();
      let formType2 = formTypeStepList[1].formType.toLowerCase();

      let step1 = formTypeStepList[0].step;
      let step2 = formTypeStepList[1].step;

      // both forms are Completed.
      if (this.isFormTypeCompleted(step1) && this.isFormTypeCompleted(step2)) {
        this.router.navigate(['thank-you'], {
          queryParamsHandling: 'preserve',
        });
        // this.router.navigate(['404']);
        return;
      }

      // both forms selected and only umuim is Completed
      if (
        this.router.url.includes('/insured/uimui-') &&
        formType1 === FormType.UM_UIM &&
        !this.isFormTypeCompleted(step2) &&
        this.isFormTypeCompleted(step1)
      ) {
        this.router.navigate(['/home'], {
          queryParamsHandling: 'preserve',
        });
        return;
      }
    }
  }

  isFormTypeCompleted(step) {
    if (
      step === RequestProgressStep.ELECTRONIC_SIGNATURE ||
      step === RequestProgressStep.NON_DIGITAL_ELECTION_DOWNLOAD ||
      step === RequestProgressStep.NON_DIGITAL_ELECTION_SHIP_FORMS
    ) {
      return true;
    }
    return false;
  }

  getRequestDetails(skipCompletionCheck: boolean = false) {
    Logger.log('Start of getRequestDetails');
    // Example URL: http://localhost:4200/insured/uimui-consent?key=CiRQ96
    const requestKey = this.getRequestKey();

    this.role = this.getUserRole();
    Logger.log('Request Key', requestKey, 'User Role', this.role);
    let baseUrl = this.configService.getConfig().requestBaseUrl;
    // Make GET HTTP call to API endpoint, save response, and return an observable
    return this.http.get(`${baseUrl}/uw/request?key=${requestKey}`).pipe(
      tap((data1) => {
        let data = data1 as any;
        if (data.request === null || data?.requestInvalidated === true) {
          Logger.log('Invalidated Request. Take the user to 404');
          this.router.navigate(['invalid'], {
            queryParamsHandling: 'preserve',
          });
          // this.router.navigate(['/404']);
          return;
        }
        this.request = data.request;

        this.formTypeStep = data.formTypeStep;
        // Only check if forms are completed when not skipping completion check
        if (!skipCompletionCheck) {
          this.checkIfFormIsCompleted();
        }
        // If formTypeStep contains an object with formType 'UMUIMPIP', treat as both UM_UIM and PIP
        if (
          Array.isArray(data.formTypeStep) &&
          data.formTypeStep.some(
            (obj: any) => (obj.formType || '').toUpperCase() === 'UMUIMPIP',
          )
        ) {
          data.formTypeStep = [
            { formType: FormType.UM_UIM, step: 'Not Started' },
            { formType: FormType.PIP, step: 'Not Started' },
          ];
        }
        // Map formType values to lower_snake_case
        if (Array.isArray(data.formTypeStep)) {
          this.requestFormTypeList = data.formTypeStep.map((obj: any) => {
            const t = obj.formType || '';
            if (t.toLowerCase() === 'umuim' || t.toLowerCase() === 'um_uim')
              return FormType.UM_UIM;
            if (t.toLowerCase() === 'pip') return FormType.PIP;
            return t.toLowerCase();
          });
        } else {
          this.requestFormTypeList = [];
        }
        Logger.log(
          'getRequestDetails. Form Type Lists.',
          this.requestFormTypeList,
        );
        Logger.log('getRequestDetails. Received response.', this.request);
      }),
      catchError((err) => {
        this.request = null;
        console.error(
          'Error fetching request details for request:',
          requestKey,
          err,
        );
        throw err;
      }),
    );
  }

  updateRequestProgress(formType, step, insuredData) {
    if (this.role == UserRole.BROKER) {
      return Promise.resolve();
    }
    this.currentFormType = formType;
    this.currentStep = step;
    return new Promise((resolve, reject) => {
      let requestKey = this.getRequestKey();
      if (!requestKey) {
        throw new Error('Request ID not found');
      }

      if (this.role == UserRole.BROKER) {
        return;
      }

      if (
        window.location.href.search('skipRequestStatusCheck=true') >= 0 &&
        (window.location.origin.toLowerCase().includes('dev.digisign') ||
          window.location.origin.toLowerCase().includes('localhost') ||
          window.location.origin.toLowerCase().includes('qa.digisign'))
      ) {
        return;
      }

      let baseUrl = this.configService.getConfig().requestBaseUrl;
      let url = `${baseUrl}/insured/requests/status/update`;

      const insuredEmail =
        insuredData?.insuredEmail ??
        this.request?.requestDetails?.accountDetail?.insuredEmail;

      let params = {
        requestKey: this.getRequestKey(),
        formType,
        step,
        insuredEmail,
        data: insuredData,
      };

      return this.http.post(url, params).subscribe(
        (res) => {
          this.setFormTypeStep(formType, step);
          Logger.log('updateRequestProgress. Received response.');
          return resolve(res);
        },
        (err) => {
          console.error(
            'Error updating request details for request:',
            requestKey,
            err,
          );
          return reject(err);
        },
      );
    });
  }

  getInitials() {
    // Check if request data is available to prevent errors when request is invalidated
    if (!this.request?.requestDetails?.accountDetail?.insuredName) {
      return '';
    }

    const name = this.request.requestDetails.accountDetail.insuredName;
    const initials = name
      .replace(/[()]/g, '') // Remove parentheses
      .split(/\s+/) // Split by whitespace
      .filter(Boolean) // Remove empty strings
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
    return initials;
  }
  getPolicyDurationInMonths(): number {
    try {
      const policyDetail = this.request?.requestDetails?.policyDetail;

      //  null check to prevent errors when request is invalidated
      if (!policyDetail || !Array.isArray(policyDetail) || !policyDetail[0]) {
        return 0;
      }

      const effectiveDate = new Date(policyDetail[0].effectiveDate);
      const expirationDate = new Date(policyDetail[0].expirationDate);

      if (isNaN(effectiveDate.getTime()) || isNaN(expirationDate.getTime())) {
        return 0;
      }

      // Calculate difference in months
      const months =
        (expirationDate.getFullYear() - effectiveDate.getFullYear()) * 12 +
        (expirationDate.getMonth() - effectiveDate.getMonth());

      // Add 1 if there are days remaining to account for partial months
      const remainingDays = expirationDate.getDate() - effectiveDate.getDate();
      return remainingDays > 0 ? months + 1 : months;
    } catch (error) {
      console.error('Error calculating policy duration:', error);
      return 0;
    }
  }

  getNamedInsured() {
    // Check if request data is available to prevent errors when request is invalidated
    if (!this.request?.requestDetails?.accountDetail?.insuredName) {
      return '';
    }

    return this.request.requestDetails.accountDetail.insuredName;
  }

  getPolicyNumberForState(inputState: string) {
    let policyDetailArray = this.request?.requestDetails?.policyDetail;

    // Check if request data is available to prevent iteration errors
    if (!policyDetailArray || !Array.isArray(policyDetailArray)) {
      return '';
    }

    for (let policy of policyDetailArray) {
      for (let coveredState of policy.coveredStates) {
        // Normalize state names to lowercase and replace spaces with hyphens
        const normalizedState = coveredState.name
          .toLowerCase()
          .replace(/\s+/g, '-');
        const withoutHyphenState = coveredState.name
          .toLowerCase()
          .replace(/\s+/g, '');
        if (
          normalizedState === inputState.toLowerCase() ||
          withoutHyphenState === inputState.toLowerCase()
        ) {
          return policy.number;
        }
      }
    }
    return '';
  }
  getIssuingCompanyForState(inputState: string) {
    let policyDetailArray = this.request?.requestDetails?.policyDetail;

    // Check if request data is available to prevent iteration errors
    if (!policyDetailArray || !Array.isArray(policyDetailArray)) {
      return '';
    }

    for (let policy of policyDetailArray) {
      for (let coveredState of policy.coveredStates) {
        // Normalize both the input state and stored state name for comparison
        const normalizedStoredState = coveredState.name
          .toLowerCase()
          .replace(/\s+/g, '-');
        const normalizedInputState = inputState
          .toLowerCase()
          .replace(/\s+/g, '-');

        const withoutHyphenState = coveredState.name
          .toLowerCase()
          .replace(/\s+/g, '');

        if (
          normalizedStoredState === normalizedInputState ||
          normalizedStoredState === withoutHyphenState
        ) {
          return policy.issuingCompany.name;
        }
      }
    }
    return '';
  }

  getLiabilityLimitForState(inputState: string) {
    let policyDetailArray = this.request?.requestDetails?.policyDetail;

    // Check if request data is available to prevent iteration errors
    if (!policyDetailArray || !Array.isArray(policyDetailArray)) {
      return '';
    }

    for (let policy of policyDetailArray) {
      for (let coveredState of policy.coveredStates) {
        // Normalize state names to lowercase and replace spaces with hyphens
        const normalizedState = coveredState.name
          .toLowerCase()
          .replace(/\s+/g, '-');
        if (normalizedState === inputState.toLowerCase()) {
          // Format liability as currency string
          const liabilityNum = Number(
            policy.liability.toString().replace(/,/g, ''),
          );
          return isNaN(liabilityNum)
            ? policy.liability
            : liabilityNum
                .toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })
                .split('.')[0];
        }
      }
    }
    return '';
  }

  getCoveredStates() {
    let policyDetailArray = [];
    let coveredStatesAr = [];
    if (this.playgroundService.isPlaygroundUMMode()) {
      return this.playgroundService.getPlaygroundCoveredUMStates();
    }
    policyDetailArray = this.request?.requestDetails?.policyDetail;

    //  null check to prevent errors when request is invalidated
    if (!policyDetailArray || !Array.isArray(policyDetailArray)) {
      return [];
    }

    if (Array.isArray(policyDetailArray)) {
      for (let policy of policyDetailArray) {
        for (let coveredState of policy.coveredStates) {
          if (!coveredStatesAr.includes(coveredState.name.toLowerCase())) {
            coveredStatesAr.push(coveredState.name.toLowerCase());
          }
        }
      }
    }
    return coveredStatesAr;
  }

  getCoveredPIPStates() {
    // Return playground selected states if in playground mode
    if (this.playgroundService.isPlaygroundMode()) {
      return this.playgroundService.getPlaygroundCoveredPIPStates();
    }
    // Original logic
    let coveredStates = this.getCoveredStates();
    coveredStates = coveredStates.filter((state) =>
      PIP_MASTER_STATES_LIST.map((s) =>
        s.toLowerCase().replace(/\s/g, ''),
      ).includes(state.toLowerCase().replace(/\s/g, '')),
    );
    return coveredStates;
  }

  getGarageStates() {
    let policyDetailArray = [];
    let garageStatesAr = [];

    // Need to add garagestates logic to playground as well
    // if (this.playgroundService.isPlaygroundUMMode()) {
    //   return this.playgroundService.getPlaygroundGarageStates();
    // }

    policyDetailArray = this.request?.requestDetails?.policyDetail;

    //  null check to prevent errors when request is invalidated
    if (!policyDetailArray || !Array.isArray(policyDetailArray)) {
      return [];
    }

    if (Array.isArray(policyDetailArray)) {
      for (let policy of policyDetailArray) {
        // Handle null, undefined, or empty garageStates arrays
        if (
          policy.garageStates &&
          Array.isArray(policy.garageStates) &&
          policy.garageStates.length > 0
        ) {
          for (let garageState of policy.garageStates) {
            if (
              garageState?.name &&
              !garageStatesAr.includes(garageState.name.toLowerCase())
            ) {
              garageStatesAr.push(garageState.name.toLowerCase());
            }
          }
        }
      }
    }
    return garageStatesAr;
  }
  getPIPGarageStates() {
    let garageStates = this.getGarageStates();
    return garageStates;
  }

  hasGarageStates(): boolean {
    const garageStates = this.getGarageStates();
    return garageStates.length > 0;
  }
  getStepForFormType(formType: string): string | null {
    if (!this.formTypeStep || !Array.isArray(this.formTypeStep)) {
      return null;
    }

    const formTypeEntry = this.formTypeStep.find((entry) => {
      const entryFormType = entry.formType?.toLowerCase();
      const targetFormType = formType.toLowerCase();
      return (
        entryFormType === targetFormType ||
        (entryFormType === 'umuim' && targetFormType === 'um_uim') ||
        (entryFormType === 'um_uim' && targetFormType === 'umuim')
      );
    });

    return formTypeEntry ? formTypeEntry.step : null;
  }

  private setFormTypeStep(formType: string, step: string) {
    // Update the in-memory step map for this form type after a successful save.
    if (!this.formTypeStep || !Array.isArray(this.formTypeStep)) {
      return;
    }

    const targetFormType = formType.toLowerCase();
    const formTypeEntry = this.formTypeStep.find((entry) => {
      const entryFormType = entry.formType?.toLowerCase();
      return (
        entryFormType === targetFormType ||
        (entryFormType === 'umuim' && targetFormType === 'um_uim') ||
        (entryFormType === 'um_uim' && targetFormType === 'umuim')
      );
    });

    if (formTypeEntry) {
      formTypeEntry.step = step;
    }
  }

  isFormTypeAlreadyCompleted(formType: string): boolean {
    // True when backend state already has this form in a completed step.
    const step = this.getStepForFormType(formType);
    return step ? this.isFormTypeCompleted(step) : false;
  }

  getFormTypeSteps(): any[] {
    return this.formTypeStep || [];
  }
  refreshRequestData(skipCompletionCheck: boolean = false) {
    return this.getRequestDetails(skipCompletionCheck);
  }
  getThankYouPageConfig(formType: string): {
    isDigital: boolean;
    isNonDigitalDownload: boolean;
    isNonDigitalMail: boolean;
    showDownloadButton: boolean;
    step: string | null;
  } {
    const step = this.getStepForFormType(formType);

    const config = {
      isDigital: false,
      isNonDigitalDownload: false,
      isNonDigitalMail: false,
      showDownloadButton: false,
      step: step,
    };

    if (!step) {
      return config;
    }

    switch (step) {
      case RequestProgressStep.ELECTRONIC_SIGNATURE:
        config.isDigital = true;
        config.showDownloadButton = true;
        break;
      case RequestProgressStep.NON_DIGITAL_ELECTION_DOWNLOAD:
        config.isNonDigitalDownload = true;
        config.showDownloadButton = true;
        break;
      case RequestProgressStep.NON_DIGITAL_ELECTION_SHIP_FORMS:
        config.isNonDigitalMail = true;
        config.showDownloadButton = false;
        break;
    }

    return config;
  }

  areAllFormsCompleted(): boolean {
    if (!this.formTypeStep || this.formTypeStep.length === 0) {
      return false;
    }

    return this.formTypeStep.every((entry) =>
      this.isFormTypeCompleted(entry.step),
    );
  }
  handleFormTypeRedirection(router: Router): boolean {
    if (!this.requestFormTypeList || this.requestFormTypeList.length === 0) {
      return false;
    }

    // Check which form types are available
    const hasUMUIM = this.requestFormTypeList.includes(FormType.UM_UIM);
    const hasPIP = this.requestFormTypeList.includes(FormType.PIP);

    // Case 1: Only UM_UIM is available (no PIP)
    if (hasUMUIM && !hasPIP) {
      const currentUrl = router.url;
      if (currentUrl.includes('/insured/pip-')) {
        router.navigate(['/insured/uimui-consent'], {
          queryParamsHandling: 'preserve',
        });
        Logger.log(
          'Redirecting to UM_UIM consent page - only UM_UIM form available',
        );
        return true;
      }
    }

    // Case 2: Only PIP is available (no UM_UIM)
    else if (hasPIP && !hasUMUIM) {
      const currentUrl = router.url;
      if (currentUrl.includes('/insured/uimui-')) {
        router.navigate(['/insured/pip-consent'], {
          queryParamsHandling: 'preserve',
        });
        Logger.log('Redirecting to PIP consent page - only PIP form available');
        return true;
      }
    }

    return false;
  }
}
